'use client';

import { useCallback, useMemo, useRef } from 'react';

import Text from '@/components/ui/Text';
import { Article } from '@/features/blog/listing/BlogCard';
import BlogCard from '@/features/blog/listing/BlogCard';
import BlogEmptyState from '@/features/blog/listing/BlogEmptyState';
import BlogPagination from '@/features/blog/listing/BlogPagination';
import BlogSearchBar from '@/features/blog/listing/BlogSearchBar';
import BlogTopSection from '@/features/blog/listing/BlogTopSection';
import {
  buildCounterText,
  filterAndSort,
  paginate,
  scrollToElement,
} from '@/features/blog/listing/utils';
import { useBlogParams } from '@/hooks/useBlogParams';
import { BlogI18n } from '@/lib/i18n/blogI18n';

const ARTICLES_PER_PAGE = 10;

const BlogAllPosts = ({ articles, featuredArticles, t }: BlogAllPostsProps) => {
  const { query, page, sort, updateParams } = useBlogParams();
  const articlesRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => filterAndSort(articles, query, sort),
    [articles, query, sort],
  );
  const { visible, totalPages, safePage, from, to } = paginate(
    filtered,
    page,
    ARTICLES_PER_PAGE,
  );

  const goToPage = (p: number) => {
    updateParams({ page: p === 1 ? null : String(p) });
    scrollToElement(articlesRef.current);
  };

  const handleQueryChange = useCallback(
    (value: string) => updateParams({ q: value || null, page: null }),
    [updateParams],
  );
  const handleSortDesc = useCallback(
    () => updateParams({ sort: null, page: null }),
    [updateParams],
  );
  const handleSortAsc = useCallback(
    () => updateParams({ sort: 'asc', page: null }),
    [updateParams],
  );
  const handleClear = useCallback(
    () => updateParams({ q: null, page: null }),
    [updateParams],
  );

  return (
    <div>
      <BlogTopSection featuredArticles={featuredArticles} t={t} />

      <div ref={articlesRef}>
        <Text as="p" variant="sectionEyebrow" className="mb-6">
          {t.allArticles}
        </Text>

        <BlogSearchBar
          query={query}
          sort={sort}
          t={t}
          onQueryChange={handleQueryChange}
          onSortDesc={handleSortDesc}
          onSortAsc={handleSortAsc}
        />

        {filtered.length === 0 ? (
          <BlogEmptyState t={t} onClear={handleClear} />
        ) : (
          <>
            <Text
              as="p"
              variant="cardEyebrow"
              className="mt-6 mb-10 text-text-secondary!"
            >
              {buildCounterText(query, filtered.length, from, to, t)}
            </Text>

            <div className="flex flex-col gap-16">
              {visible.map((article) => (
                <BlogCard key={article.slug} article={article} />
              ))}
            </div>

            <BlogPagination
              page={safePage}
              totalPages={totalPages}
              onGoToPage={goToPage}
              t={t}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BlogAllPosts;

type BlogAllPostsProps = {
  articles: Article[];
  featuredArticles: Article[];
  t: BlogI18n;
};
